import React, { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Clock, ArrowRight, ArrowLeft, Tag } from 'lucide-react';
import axios from 'axios';

const API = "/api";

function renderContent(content) {
  if (!content) return null;
  const lines = content.split('\n');
  const result = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }

    if (line.startsWith('## ')) {
      result.push(<h2 key={i} className="font-serif text-2xl font-bold text-[#0f172a] mt-10 mb-4">{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      result.push(<h3 key={i} className="font-bold text-lg text-[#0f172a] mt-6 mb-3">{line.slice(4)}</h3>);
    } else if (line.startsWith('- ') || line.startsWith('✅') || line.startsWith('❌')) {
      const items = [];
      while (i < lines.length && (lines[i].trim().startsWith('- ') || lines[i].trim().startsWith('✅') || lines[i].trim().startsWith('❌'))) {
        const text = lines[i].trim().replace(/^[-✅❌]\s*/, '');
        items.push(
          <li key={i} className="flex items-start gap-2 text-[#374151]">
            <span className="w-1.5 h-1.5 bg-[#D4A017] rounded-full mt-2 flex-shrink-0"></span>
            <span dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
          </li>
        );
        i++;
      }
      result.push(<ul key={`list-${i}`} className="space-y-2 my-4 pl-1">{items}</ul>);
      continue;
    } else if (/^\d+\./.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\./.test(lines[i].trim())) {
        const text = lines[i].trim().replace(/^\d+\.\s*/, '');
        items.push(
          <li key={i} className="text-[#374151]">
            <span dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
          </li>
        );
        i++;
      }
      result.push(<ol key={`ol-${i}`} className="list-decimal pl-6 space-y-2 my-4">{items}</ol>);
      continue;
    } else if (line.startsWith('[') && line.includes('](/')) {
      const match = line.match(/\[(.+?)\]\((.+?)\)/);
      if (match) {
        result.push(
          <div key={i} className="my-6">
            <Link to={match[2].split('?')[0]} className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-6 py-3 rounded-lg transition-colors">
              {match[1]} <ArrowRight size={16} />
            </Link>
          </div>
        );
      }
    } else if (line.startsWith('**') && line.endsWith('**')) {
      result.push(<p key={i} className="font-bold text-[#0f172a] mt-4 mb-2">{line.slice(2, -2)}</p>);
    } else {
      result.push(
        <p key={i} className="text-[#374151] leading-relaxed my-3"
          dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>') }}
        />
      );
    }
    i++;
  }
  return result;
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await axios.get(`${API}/blog/${slug}`);
        setArticle(data);
        // Fetch related
        const { data: relData } = await axios.get(`${API}/blog`, { params: { per_page: 3, category: data.category } });
        setRelated((relData.articles || []).filter(a => a.slug !== slug).slice(0, 2));
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  if (notFound) return <Navigate to="/blog" replace />;

  if (loading) {
    return (
      <div className="bg-[#0f172a] pt-28 pb-16 min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4A017] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#94a3b8]">Artikel laden...</p>
        </div>
      </div>
    );
  }

  if (!article) return null;

  return (
    <>
      <SEOHead
        title={article.meta_title || article.title}
        description={article.meta_description}
        keywords={(article.tags || []).join(', ')}
        canonical={`/blog/${article.slug}`}
        ogImage={article.image}
        schema={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": article.title,
          "description": article.meta_description,
          "image": article.image,
          "datePublished": article.published_at,
          "author": { "@type": "Organization", "name": "IMPERIA AI" },
          "publisher": { "@type": "Organization", "name": "IMPERIA AI" }
        }}
      />

      {/* Article Hero */}
      <div className="bg-[#0f172a] pt-28 pb-0 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4A017] via-[#f5c842] to-[#D4A017]" />
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 39px,#D4A017 39px,#D4A017 40px),repeating-linear-gradient(90deg,transparent,transparent 39px,#D4A017 39px,#D4A017 40px)' }} />
        <div className="container-custom relative z-10 max-w-3xl mx-auto pb-0">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-[#D4A017] text-sm mb-6 transition-colors">
            <ArrowLeft size={14} /> Terug naar blog
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#D4A017] text-[#0f172a] text-xs font-bold px-3 py-1 rounded-full">{article.category}</span>
            <span className="text-[#94a3b8] text-sm flex items-center gap-1"><Clock size={12} />{article.read_time}</span>
            <span className="text-[#94a3b8] text-sm">
              {new Date(article.published_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            {article.title}
          </h1>
        </div>
        <div className="container-custom relative z-10 max-w-3xl mx-auto mt-8">
          <img src={article.image} alt={article.title} className="w-full h-64 md:h-80 object-cover rounded-t-2xl" />
        </div>
      </div>

      {/* Content */}
      <div className="bg-white py-12 md:py-16">
        <div className="container-custom max-w-3xl mx-auto">
          {article.excerpt && (
            <p className="text-lg text-[#64748b] leading-relaxed border-l-4 border-[#D4A017] pl-5 mb-8 italic">
              {article.excerpt}
            </p>
          )}
          <div className="prose max-w-none">
            {renderContent(article.content)}
          </div>

          {/* Tags */}
          {article.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-100">
              <Tag size={14} className="text-[#64748b] mt-0.5" />
              {article.tags.map(tag => (
                <span key={tag} className="bg-[#f8fafc] text-[#64748b] text-xs px-3 py-1 rounded-full border border-gray-200">{tag}</span>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-10 bg-[#0f172a] rounded-2xl p-8 text-center">
            <h3 className="font-serif text-2xl font-bold text-white mb-3">Klaar om in actie te komen?</h3>
            <p className="text-[#94a3b8] mb-6">Wij helpen je dit implementeren. Vraag een vrijblijvende offerte aan.</p>
            <Link to="/offerte">
              <Button className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-8 py-4">
                Offerte aanvragen <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-14">
              <h3 className="font-bold text-xl text-[#0f172a] mb-6">Meer lezen</h3>
              <div className="grid sm:grid-cols-2 gap-6">
                {related.map(rel => (
                  <Link key={rel.slug} to={`/blog/${rel.slug}`} className="group flex gap-4 bg-[#f8fafc] rounded-xl p-4 hover:bg-[#D4A017]/5 transition-colors">
                    <img src={rel.image} alt={rel.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                    <div>
                      <span className="text-xs text-[#D4A017] font-semibold">{rel.category}</span>
                      <p className="text-sm font-bold text-[#0f172a] group-hover:text-[#D4A017] transition-colors line-clamp-2 mt-1">{rel.title}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
